import {
  Avatar,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  usePersistMuiTableState,
  dateFormatterDayMonthLongYearAndHourMinute2Digits,
  getPreviousAndNextIds,
} from "@qivia/ui";
import { getFilteredDataGridRows } from "@qivia/ui/src/designSystem/components/materialUiTable/getFilteredDataGridRows";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import "../../../../i18n/i18n";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  selectVehicleSessionsList,
  selectVehicleSessionsListStatus,
} from "../fleetSlice";
import {
  formatDateTime,
  formatMileage,
  getDateTime,
} from "../../../../libSlice/materialUiFormats";
import type { VehicleSession, VehicleSessionDisplayed } from "../fleetApi";
import { CleaningTag } from "./cleaningTag";

const pageName = "fleet.vehicleSessions";
const gridStorageKey = "vehicle-sessions-grid";

type WithId = VehicleSessionDisplayed & { id: string };

interface SharedInterfaceVehicleSessionsProps {
  sidePanelAction?: (
    row: Partial<VehicleSessionDisplayed> & { id?: string },
  ) => void;
  sidePanel?: (props: {
    session: VehicleSession;
    previousSessionId: string | null;
    nextSessionId: string | null;
  }) => JSX.Element;
}

export const SharedInterfaceVehicleSessions = (
  props: SharedInterfaceVehicleSessionsProps,
) => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const vehicleSessionsList = useAppSelector(selectVehicleSessionsList);
  const vehicleSessionsListStatus = useAppSelector(
    selectVehicleSessionsListStatus,
  );
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    gridStorageKey,
    pageName,
  );

  const [listDisplayed, setListDisplayed] = useState<
    VehicleSessionDisplayed[] | null
  >(null);

  const findKeysToTranslate = useCallback(
    (key: keyof VehicleSessionDisplayed, value: string) => {
      switch (key) {
        case "startOfUsage":
        case "endOfUsage":
          return dateFormatterDayMonthLongYearAndHourMinute2Digits(
            new Date(value),
          );
        case "isCabinCleaned":
        case "isTrunkCleaned":
          return t(`fleet.vehicleSessions.value.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "startOfUsage",
        "endOfUsage",
        "isCabinCleaned",
        "isTrunkCleaned",
      ] as (keyof VehicleSessionDisplayed)[],
    [],
  );

  const vehicleSessionsDisplayed: (VehicleSession & {
    id: string;
  })[] = useMemo(() => {
    return vehicleSessionsList.map((session) => {
      let newDict: VehicleSession & { id: string } = {
        ...session,
        id: session.uuid,
      };
      Object.entries(session).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof VehicleSessionDisplayed)) {
          newDict = {
            ...newDict,
            [key]:
              value !== null
                ? findKeysToTranslate(
                    key as keyof VehicleSessionDisplayed,
                    value as string,
                  )
                : "",
          };
        }
      });
      return newDict;
    });
  }, [vehicleSessionsList, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (vehicleSessionsListStatus === "success") {
      setListDisplayed(vehicleSessionsDisplayed);
    }
  }, [vehicleSessionsDisplayed, vehicleSessionsListStatus]);

  const searchBarProps = {
    values: vehicleSessionsDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "driverUuid" as const,
      "vehicleUuid" as const,
      "restitutions" as const,
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("fleet.vehicleSessions.view.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof VehicleSessionDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      startOfUsage: {
        type: "date",
        width: 210,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      endOfUsage: {
        type: "date",
        width: 210,
        valueGetter: getDateTime,
        valueFormatter: (value: string | Date) => formatDateTime(value) || "-",
      },
      registrationNumber: {
        type: "string",
        valueFormatter: (value: string | null) => value ?? "-",
      },
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string) => {
          const initials = value?.split(" ").map((word) => word.charAt(0));
          return (
            value && (
              <StyledRow>
                <Avatar
                  color={"mint"}
                  size={"S"}
                  firstName={initials[0]}
                  lastName={initials[initials.length - 1]}
                />
                <Spacer x={0.75} />
                {value}
              </StyledRow>
            )
          );
        },
      },
      departureLocation: {
        type: "string",
        width: 320,
        valueFormatter: (value: string | null) => value ?? "-",
      },
      furthestDestination: {
        type: "string",
        width: 320,
        valueFormatter: (value: string | null) => value ?? "-",
      },
      mileage: {
        type: "number",
        valueGetter: (value: number | null) => value,
        valueFormatter: (value: number | null) =>
          value !== null ? formatMileage(value) : "-",
      },
      isCabinCleaned: {
        type: "string",
        valueFormatter: (value: string) => (
          <StyledCenteredRow>
            {value ? (
              <CleaningTag
                isCleaned={value === t(`fleet.vehicleSessions.value.${true}`)}
              />
            ) : (
              "-"
            )}
          </StyledCenteredRow>
        ),
      },
      isTrunkCleaned: {
        type: "string",
        valueFormatter: (value: string) => (
          <StyledCenteredRow>
            {value ? (
              <CleaningTag
                isCleaned={value === t(`fleet.vehicleSessions.value.${true}`)}
              />
            ) : (
              "-"
            )}
          </StyledCenteredRow>
        ),
      },
      observations: {
        type: "string",
        width: 380,
        valueFormatter: (value: string | null) => value ?? "-",
      },
    };
  }, [t]);

  const currentSession = useMemo(
    () => vehicleSessionsList.find((s) => s.uuid === params.id) ?? null,
    [vehicleSessionsList, params.id],
  );

  const navigationId = useMemo(() => {
    const visibleRows = getFilteredDataGridRows(apiRef) as WithId[];
    const list =
      visibleRows.length > 0
        ? visibleRows
        : ((listDisplayed ?? []) as WithId[]);

    const { previousId, nextId } = getPreviousAndNextIds(
      list,
      params.id ?? null,
    );

    return { previousSessionId: previousId, nextSessionId: nextId };
  }, [apiRef, params.id, listDisplayed]);

  if (!listDisplayed) {
    return <LaunchPage hasBorderRadius={true} />;
  }

  return (
    <StyledContainer>
      <MaterialUiTable<keyof VehicleSessionDisplayed, VehicleSessionDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={props.sidePanelAction}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        rowToSendToTop={params.id ?? null}
      />
      {props.sidePanel && currentSession ? (
        props.sidePanel({
          session: currentSession,
          previousSessionId: navigationId.previousSessionId,
          nextSessionId: navigationId.nextSessionId,
        })
      ) : (
        <></>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledCenteredRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
